/**
 * Audit History Dialog Component
 * 
 * Main dialog component for displaying timesheet audit history.
 * Includes a "currently pending with" banner when a timesheet is awaiting approval.
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useAuditHistory } from '@/hooks/useAuditHistory';
import { AuditSummary } from './AuditSummary';
import { AuditTimeline } from './AuditTimeline';
import { Loader2, AlertCircle, FileX, Clock, CheckCircle2, UserCheck, GitBranch } from 'lucide-react';
import { format } from 'date-fns';
import type { Timesheet } from '@/types/timesheet';

interface AuditHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  timesheetId: number;
  employeeName: string;
  weekEnding: Date;
  timesheet?: Timesheet;
}

/**
 * Banner showing where the timesheet currently sits in the approval chain.
 * Only rendered when the timesheet is in 'submitted' status.
 */
function CurrentStatusBanner({ timesheet }: { timesheet: Timesheet }) {
  const { status, approverName, delegatedApproverName, currentStageName, currentStageOrder, totalStages } = timesheet;

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800 px-4 py-3 mb-4">
        <div className="p-1.5 rounded-full bg-green-100 dark:bg-green-900/40">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-green-800 dark:text-green-200">Timesheet Approved</p>
          {approverName && (
            <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">
              Approved by <span className="font-medium">{approverName}</span>
            </p>
          )}
        </div>
        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-200 dark:border-green-700 flex-shrink-0">
          Approved
        </Badge>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-4 py-3 mb-4">
        <div className="p-1.5 rounded-full bg-red-100 dark:bg-red-900/40">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-800 dark:text-red-200">Timesheet Rejected — Action Required</p>
          {approverName && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
              Rejected by <span className="font-medium">{approverName}</span>
            </p>
          )}
        </div>
        <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700 flex-shrink-0">
          Rejected
        </Badge>
      </div>
    );
  }

  if (status !== 'submitted') {
    return null;
  }

  // Determine who it's currently with
  const effectiveApprover = delegatedApproverName || approverName;
  const isDelegated = !!delegatedApproverName && delegatedApproverName !== approverName;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 px-4 py-3 mb-4">
      <div className="p-1.5 rounded-full bg-blue-100 dark:bg-blue-900/40 mt-0.5">
        <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
          Awaiting Approval
        </p>

        {/* Stage progress */}
        {currentStageName && (
          <div className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-300">
            <GitBranch className="h-3 w-3 flex-shrink-0" />
            <span>
              Stage: <span className="font-medium">{currentStageName}</span>
              {currentStageOrder != null && totalStages != null && (
                <span className="text-blue-500 dark:text-blue-400"> ({currentStageOrder} of {totalStages})</span>
              )}
            </span>
          </div>
        )}

        {/* Who it's waiting for */}
        {effectiveApprover ? (
          <div className="flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-300">
            {isDelegated ? (
              <UserCheck className="h-3 w-3 flex-shrink-0" />
            ) : (
              <Clock className="h-3 w-3 flex-shrink-0" />
            )}
            <span>
              Waiting for <span className="font-semibold text-blue-800 dark:text-blue-200">{effectiveApprover}</span>
              {isDelegated && approverName && (
                <span className="text-blue-500 dark:text-blue-400"> (delegated from {approverName})</span>
              )}
            </span>
          </div>
        ) : (
          <p className="text-xs text-blue-600 dark:text-blue-400">
            Pending assignment to an approver
          </p>
        )}
      </div>
      <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-700 flex-shrink-0 mt-0.5">
        Pending
      </Badge>
    </div>
  );
}

export function AuditHistoryDialog({
  open,
  onOpenChange,
  timesheetId,
  employeeName,
  weekEnding,
  timesheet,
}: AuditHistoryDialogProps) {
  const { auditEntries, auditSummary, hasHistory, isLoading, error } = useAuditHistory(timesheetId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Timesheet History — Week Ending {format(weekEnding, 'MMM d, yyyy')}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Employee: {employeeName}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-1">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-lg bg-blue-50">
                  <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-2">Loading History</h3>
              <p className="text-sm text-muted-foreground">
                Fetching approval history...
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-lg bg-red-50">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold mb-2 text-red-600">Error Loading History</h3>
              <p className="text-sm text-red-500">
                {error}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Current status / pending-with banner — always shown when timesheet is provided */}
              {timesheet && (
                <CurrentStatusBanner timesheet={timesheet} />
              )}

              {hasHistory ? (
                <>
                  <AuditSummary summary={auditSummary} />
                  <AuditTimeline entries={auditEntries} />
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="flex justify-center mb-4">
                    <div className="p-3 rounded-lg bg-gray-50">
                      <FileX className="h-8 w-8 text-gray-600" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">No History Available</h3>
                  <p className="text-sm text-muted-foreground">
                    This timesheet doesn't have any recorded history yet.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
